import { useCallback, useEffect, useState } from 'react';
import { savedInspectionsStorageV2 } from '@/store/idbStorage/savedInspectionsStorageV2';
import { Inspection } from '@/types/Inspection.type';

/**
 * Fetches the saved inspection from savedInspectionsStorageV2 by id.
 */
export function useSavedInspection(inspectionId?: string) {
    const [savedInspection, setSavedInspection] = useState<Inspection | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchSavedInspection = useCallback(async (id: string | null | undefined) => {
        if (!id) return;
        setIsLoading(true);
        setError(null);
        try {
            const inspection = await savedInspectionsStorageV2.get(id);
            setSavedInspection(inspection);
        } catch (err) {
            setError(err instanceof Error ? err : new Error(String(err)));
            setSavedInspection(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!inspectionId) {
            setSavedInspection(null);
            setError(null);
            setIsLoading(false);
            return;
        }
        fetchSavedInspection(inspectionId);
    }, [inspectionId, fetchSavedInspection]);

    const refetch = useCallback(() => {
        if (inspectionId) {
            return fetchSavedInspection(inspectionId);
        }
        return Promise.resolve();
    }, [inspectionId, fetchSavedInspection]);

    return { savedInspection, isLoading, error, refetch };
}
